// src/pages/league-dashboard/DraftManagement.js - Draft Management Page
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Play, Calendar, Users, ArrowRight, AlertCircle, 
  Settings, FileText, CheckCircle
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import TeamPageHeader from '../../components/common/TeamPageHeader';

const DraftManagement = ({ league, userTeam }) => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [draftPicks, setDraftPicks] = useState([]);
  const [draftOrder, setDraftOrder] = useState(null);
  const [draftMode, setDraftMode] = useState('offline');
  const [leagueStatus, setLeagueStatus] = useState(null);

  useEffect(() => {
    loadDraftData();
  }, [leagueId]);

  const loadDraftData = async () => {
    try {
      setLoading(true);
      
      // Get league info to check draft mode and status
      const leagueData = await leaguesAPI.getLeagueDetails(leagueId);
      if (leagueData && leagueData.success && leagueData.league) {
        const leagueInfo = leagueData.league;
        setDraftMode(leagueInfo.draft_mode || 'offline');
        setLeagueStatus(leagueInfo.league_status || 'setup');
      }

      // Get user's draft picks — draft-room view: full unfiltered list (no
      // tradeable_only), scoped to picks the team currently owns. The
      // consolidated endpoint (§4.5.24) returns the merged superset under
      // `picks` (the prior `draft_picks` key never existed on the response,
      // so this preview was silently empty before). Keeps selection /
      // overall_pick_number for display; includes consumed/past picks.
      if (userTeam?.team_id) {
        const response = await leaguesAPI.getTeamDraftPicks(leagueId, userTeam.team_id);
        if (response?.picks) {
          setDraftPicks(response.picks.filter(p => p.is_owned));
        }
      }
    } catch (error) {
      console.error('Error loading draft data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterLiveDraft = () => {
    navigate(`/leagues/${leagueId}/draft/live`);
  };

  const isCommissioner = league?.role === 'commissioner';
  const isLiveDraft = draftMode === 'live';
  const isDraftStatus = leagueStatus === 'draft';

  if (loading) {
    return (
      <div className={dynastyTheme.components.page}>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`} />
            <span className={`${dynastyTheme.classes.text.white} text-lg`}>Loading draft information...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={dynastyTheme.components.page}>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <TeamPageHeader
          title="Draft"
          logoUrl={userTeam?.team_logo_url}
          teamName={userTeam?.team_name}
          teamColors={userTeam?.team_colors}
          teamMotto={userTeam?.team_motto}
          badges={false}
          subtitle={
            <span className={dynastyTheme.classes.text.neutralLight}>
              {isLiveDraft
                ? 'Real-time live draft room with WebSocket connectivity'
                : 'Offline draft - picks can be made asynchronously'}
            </span>
          }
        />

        {/* Draft Status Card */}
        <div className={`${dynastyTheme.components.card.base} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`${dynastyTheme.components.heading.h3} flex items-center gap-2`}>
              <Calendar className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              Draft Status
            </h2>
            {isLiveDraft && isDraftStatus && (
              <button
                onClick={handleEnterLiveDraft}
                className={`${dynastyTheme.components.button.primary} px-6 py-2 flex items-center gap-2`}
              >
                <Play className="w-4 h-4" />
                Enter Live Draft Room
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-lg`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-1`}>Draft Mode</div>
              <div className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
                {isLiveDraft ? 'Live' : 'Offline'}
              </div>
            </div>
            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-lg`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-1`}>League Status</div>
              <div className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
                {leagueStatus || 'setup'}
              </div>
            </div>
            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-lg`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-1`}>Your Draft Picks</div>
              <div className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
                {draftPicks.length}
              </div>
            </div>
          </div>

          {isLiveDraft && !isDraftStatus && (
            <div className={`mt-4 p-4 ${dynastyTheme.classes.bg.darkLighter} rounded-lg flex items-start gap-3`}>
              <AlertCircle className={`w-5 h-5 ${dynastyTheme.classes.text.warning} mt-0.5`} />
              <div>
                <div className={`font-semibold ${dynastyTheme.classes.text.white} mb-1`}>
                  Draft Not Started
                </div>
                <div className={dynastyTheme.classes.text.neutralLight}>
                  {isCommissioner 
                    ? 'Visit Season Setup to configure and start the live draft.'
                    : 'The commissioner needs to start the draft from Season Setup.'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Commissioner Actions */}
        {isCommissioner && (
          <div className={`${dynastyTheme.components.card.base} p-6`}>
            <h2 className={`${dynastyTheme.components.heading.h3} mb-4 flex items-center gap-2`}>
              <Settings className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              Commissioner Actions
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/leagues/${leagueId}`, { state: { activeSection: 'draft-setup' } })}
                className={`w-full ${dynastyTheme.components.button.secondary} p-4 flex items-center justify-between`}
              >
                <div className="flex items-center gap-3">
                  <Settings className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                  <span className="font-medium">Configure Draft Settings</span>
                </div>
                <ArrowRight className="w-5 h-5" />
              </button>
              {isLiveDraft && isDraftStatus && (
                <button
                  onClick={handleEnterLiveDraft}
                  className={`w-full ${dynastyTheme.components.button.primary} p-4 flex items-center justify-between font-semibold`}
                >
                  <div className="flex items-center gap-3">
                    <Play className="w-5 h-5" />
                    <span>Enter Live Draft Room</span>
                  </div>
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Draft Picks Preview */}
        {draftPicks.length > 0 && (
          <div className={`${dynastyTheme.components.card.base} p-6`}>
            <h2 className={`${dynastyTheme.components.heading.h3} mb-4 flex items-center gap-2`}>
              <Users className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              Your Draft Picks
            </h2>
            <div className="space-y-2">
              {draftPicks.slice(0, 10).map((pick, idx) => (
                <div 
                  key={idx}
                  className={`${dynastyTheme.classes.bg.darkLighter} p-3 rounded-lg flex items-center justify-between`}
                >
                  <div>
                    <div className={`font-medium ${dynastyTheme.classes.text.white}`}>
                      Round {pick.round}, Pick {pick.pick_in_round}
                    </div>
                    {pick.league_player_id && (
                      <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                        Player selected
                      </div>
                    )}
                  </div>
                  {pick.league_player_id ? (
                    <CheckCircle className={`w-5 h-5 ${dynastyTheme.classes.text.success}`} />
                  ) : (
                    <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Pending</div>
                  )}
                </div>
              ))}
              {draftPicks.length > 10 && (
                <div className={`text-center pt-2 ${dynastyTheme.classes.text.neutralLight}`}>
                  ... and {draftPicks.length - 10} more picks
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftManagement;

